<template>
  <HeadlessListbox :id="name" as="div">
    <HeadlessListboxLabel
      v-if="label"
      class="text-background-fg/70 mb-1 block text-left text-xs font-medium"
    >
      {{ label }}
    </HeadlessListboxLabel>

    <div ref="listboxReferenceRef">
      <HeadlessListboxButton as="template">
        <slot name="button">
          <button
            type="button"
            :class="[
              'border-1 flex w-full flex-nowrap items-center justify-between rounded-md px-3 py-2 text-sm font-semibold focus:outline-none sm:text-sm',
              hasError
                ? 'border-error text-error placeholder-error focus:border-error focus:ring-error'
                : 'focus:border-primary focus:ring-primary',
            ]"
          >
            <span class="mr-2 line-clamp-1">
              {{ selectedLabel }}
            </span>
            <IconHeroiconsSolidChevronDown
              class="text-background-fg/40 rounded-full"
            />
          </button>
        </slot>
      </HeadlessListboxButton>
    </div>

    <Teleport to="body">
      <div ref="listboxOptionsRef" :style="floatingStyles">
        <Transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <HeadlessListboxOptions
            class="bg-background mb-6 max-h-60 w-full overflow-y-auto rounded-lg shadow"
          >
            <HeadlessListboxOption
              v-for="option in options"
              :key="option.value ?? undefined"
            >
              <div
                :class="[
                  modelValue === option.value
                    ? 'bg-background-fg/5'
                    : 'text-background-fg/50 hover:bg-background-fg/1',
                  'flex cursor-pointer items-center justify-start px-3 py-2',
                ]"
                @click="setModelValue(option.value)"
              >
                <Component
                  :is="option.icon"
                  v-if="option.icon"
                  class="mr-2"
                />
                <span
                  class="line-clamp-1 text-left text-sm font-semibold"
                >
                  {{ option.label }}
                </span>
              </div>
            </HeadlessListboxOption>
          </HeadlessListboxOptions>
        </Transition>
      </div>
    </Teleport>
  </HeadlessListbox>
</template>

<script
  setup
  lang="ts"
  generic="
    TValue extends string | number | undefined | null
  "
>
import type { Option } from "@/composables/options/options"
import {
  useFloating,
  offset,
  flip,
  autoUpdate,
  size,
} from "@floating-ui/vue"

const props = defineProps<{
  options: Option<TValue>[]
  label?: string
  modelValue: TValue
  errorMessage?: string
  name?: string
}>()

const emit = defineEmits<{
  (event: "update:model-value", params: TValue): void
}>()

const hasError = computed(() => !!props.errorMessage)

const setModelValue = (newValue: TValue) => {
  emit("update:model-value", newValue)
}

const selectedLabel = computed(
  () =>
    props.options.find(
      ({ value }) => value === props.modelValue
    )?.label || props.modelValue
)

const listboxOptionsRef = ref()
const listboxReferenceRef = ref()

const { floatingStyles } = useFloating(
  listboxReferenceRef,
  listboxOptionsRef,

  {
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          })
        },
      }),
    ],
  }
)
</script>
